import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Save, Building, Globe, Mail, Phone, Tag } from '../../components/CustomIcons';

const CATEGORIES = ['Higher Education', 'Technology Certifications', 'Trucking & Logistics', 'Healthcare', 'Skilled Trades', 'Business', 'Other'];

export default function ProviderSettings() {
  const { authFetch } = useAuth();
  const [form, setForm] = useState({ name: '', category: '', description: '', website: '', contact_email: '', contact_phone: '', tags: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    authFetch('/marketplace/provider/settings')
      .then(r => {
        const p = r.data || {};
        setForm({
          name: p.name || '',
          category: p.category || '',
          description: p.description || '',
          website: p.website || '',
          contact_email: p.contact_email || '',
          contact_phone: p.contact_phone || '',
          tags: (p.tags || []).join(', '),
        });
      })
      .catch(e => console.error('Load settings error:', e))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await authFetch('/marketplace/provider/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error('Save settings error:', e); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ padding: '32px', background: 'var(--bg-page)', minHeight: '100vh' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>
        {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: '52px', borderRadius: '10px', marginBottom: '14px' }} />)}
      </div>
    </div>;
  }

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="provider-settings">
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <div className="overline" style={{ marginBottom: '4px' }}>Provider Portal</div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Organization Settings</h1>
        </div>

        <div className="card" style={{ padding: '28px' }}>
          <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: '0 0 20px 0' }}>
            Organization Profile
          </h2>

          {[
            { key: 'name', label: 'Organization Name', icon: Building, placeholder: 'Dallas College' },
            { key: 'website', label: 'Website', icon: Globe, placeholder: 'https://yourorganization.edu' },
            { key: 'contact_email', label: 'Contact Email', icon: Mail, placeholder: 'programs@yourorg.edu' },
            { key: 'contact_phone', label: 'Contact Phone', icon: Phone, placeholder: '(555) 000-0000' },
          ].map(({ key, label, icon: Icon, placeholder }) => (
            <div key={key} style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>
                {label}
              </label>
              <div style={{ position: 'relative' }}>
                <Icon size={15} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input className="input-dark" value={form[key]} onChange={e => set(key, e.target.value)}
                  placeholder={placeholder} style={{ paddingLeft: '38px' }}
                  data-testid={`settings-${key}`} />
              </div>
            </div>
          ))}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Category</label>
            <select className="input-dark" value={form.category} onChange={e => set('category', e.target.value)}>
              <option value="">Select category...</option>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Description</label>
            <textarea className="input-dark" value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Tell learners about your organization, programs, and outcomes..." rows={4}
              style={{ resize: 'vertical', lineHeight: 1.6 }}
              data-testid="settings-description" />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>
              <Tag size={13} style={{ display: 'inline', marginRight: '4px' }} />
              Tags (comma-separated)
            </label>
            <input className="input-dark" value={form.tags} onChange={e => set('tags', e.target.value)}
              placeholder="GED Prep, Workforce Certificates, Community College" />
          </div>

          <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ width: '100%', justifyContent: 'center' }}
            data-testid="save-settings-btn">
            {saved ? '✓ Saved!' : saving ? 'Saving...' : (<><Save size={15} /> Save Changes</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
